import { useMemo } from 'react';
import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';
import { useSMLCountryParam } from './useSMLCountryParam';

export interface ArtistAdditionsStats {
  addedToPlaylists: number;
  addedToPlaylistsChange: number | null;
  removedFromPlaylists: number;
  removedFromPlaylistsChange: number | null;
}

export interface ArtistAppearancesStats {
  appearedInPlaylists: number;
  appearedInPlaylistsChange: number | null;
}

export interface ArtistStats extends ArtistAdditionsStats, ArtistAppearancesStats {}

interface UseArtistStatsResult {
  /** Additions stats (gain/loss) - available as soon as that query completes */
  additionsStats: ArtistAdditionsStats | null;
  /** Appearances stats (visibility) - available as soon as that query completes */
  appearancesStats: ArtistAppearancesStats | null;
  /** Combined stats - only available when both queries complete */
  stats: ArtistStats | null;
  isAdditionsLoading: boolean;
  isAppearancesLoading: boolean;
  isLoading: boolean;
  error: unknown;
}

/**
 * Hook to fetch artist stats (playlist additions, removals, appearances) for a specific date.
 * Uses cached endpoints for optimal performance.
 */
export function useArtistStats(artistId: string | undefined, selectedDate: Date | undefined): UseArtistStatsResult {
  const trpcMusicAPI = useTRPCAPI();
  const { countryCode } = useSMLCountryParam();

  // Fetch playlist additions (gain/loss scores)
  const {
    data: additionsData,
    isLoading: isAdditionsLoading,
    error: additionsError,
  } = useTRPCQuery(
    trpcMusicAPI.artists.playlistAdditions.queryOptions(
      {
        artistId: artistId ?? '',
        date: selectedDate,
        countryCode,
      },
      {
        enabled: Boolean(artistId),
      }
    )
  );

  // Fetch playlist appearances (visibility score)
  const {
    data: appearancesData,
    isLoading: isAppearancesLoading,
    error: appearancesError,
  } = useTRPCQuery(
    trpcMusicAPI.artists.playlistAppearances.queryOptions(
      {
        artistId: artistId ?? '',
        date: selectedDate,
        countryCode,
      },
      {
        enabled: Boolean(artistId),
      }
    )
  );

  const additionsStats = useMemo<ArtistAdditionsStats | null>(() => {
    if (!additionsData) {
      return null;
    }

    return {
      addedToPlaylists: additionsData.addedToPlaylists.current,
      addedToPlaylistsChange: additionsData.addedToPlaylists.percentageChange,
      removedFromPlaylists: additionsData.removedFromPlaylists.current,
      removedFromPlaylistsChange: additionsData.removedFromPlaylists.percentageChange,
    };
  }, [additionsData]);

  const appearancesStats = useMemo<ArtistAppearancesStats | null>(() => {
    if (!appearancesData) {
      return null;
    }

    return {
      appearedInPlaylists: appearancesData.appearedInPlaylists.current,
      appearedInPlaylistsChange: appearancesData.appearedInPlaylists.percentageChange,
    };
  }, [appearancesData]);

  const stats = useMemo<ArtistStats | null>(() => {
    if (!additionsStats || !appearancesStats) {
      return null;
    }

    return {
      ...additionsStats,
      ...appearancesStats,
    };
  }, [additionsStats, appearancesStats]);

  return {
    additionsStats,
    appearancesStats,
    stats,
    isAdditionsLoading,
    isAppearancesLoading,
    isLoading: isAdditionsLoading || isAppearancesLoading,
    error: additionsError || appearancesError,
  };
}
